import React from "react";
import { ReactComponent as ViewDetailsSVG } from "@assets/icons/ic_document_search.svg";
import { ReactComponent as EditSVG } from "@assets/icons/ic_edit.svg";
import { ReactComponent as TrashSVG } from "@assets/icons/ic_trash.svg";
import { ReactComponent as StartPickingSVG } from "@assets/icons/ic_start_picking.svg";
import { ReactComponent as PDFIcon } from "@assets/icons/ic_pdf.svg";
import { ReactComponent as PurchsaeOrderSVG } from "@assets/icons/ic_purchase.svg";
import { ReactComponent as SalesOrderSVG } from "@assets/icons/ic_salesOrder_cart.svg";
import { ReactComponent as StartReceivingSVG } from "@assets/icons/ic_start_receiving.svg";
import { ReactComponent as EmailSVG } from "@assets/icons/ic_email.svg";
import { ReactComponent as ProduceSVG } from "@assets/icons/ic_bom_produce.svg";
import { ReactComponent as AllProductsIcon } from "@assets/icons/sidebar/ic_small_bag.svg";
import { ReactComponent as DuplicateSVG } from "@assets/icons/ic_duplicate.svg";
import { ReactComponent as CopySVG } from "@assets/icons/ic_copy.svg";
import { Ban, Barcode } from "lucide-react";
import BaseTooltip from "@reusables/BaseTooltip";


enum TinyActionIconType {
    Edit,
    Delete,
    ViewDetails,
    StartPicking,
    PDF,
    PurchaseOrder,
    SalesOrder,
    StartReceiving,
    Email,
    Produce,
    Product,
    Duplicate,
    Barcode,
    Copy,
    Cancel,
    //Receive,
    // ... add other types if needed
}

export interface BaseTinyActionIconProps {
    type: TinyActionIconType;
    onClick: () => void;
    variant?: "gradient" | "bordered"; // New prop for the variant
    disabled?: boolean;
    className?: string;
    tooltip?: string;
}

const getDefaultIconProps = (variant: "gradient" | "bordered") => {
    return variant === "gradient"
        ? { className: "hover:opacity-80" }
        : {
            width: "16px",
            height: "16px",
            className: "text-actionableIcons",
            cursor: "pointer"
        };
};

type IconConfig = {
    component: React.ComponentType<any>;
    testId: string;
    getProps?: (variant: "gradient" | "bordered") => Record<string, any>;
};

const iconConfigMap: Record<TinyActionIconType, IconConfig> = {
    [TinyActionIconType.Edit]: {
        component: EditSVG,
        testId: "edit",
        getProps: (variant) => ({
            ...getDefaultIconProps(variant),
            style: { transform: "scale(.8)" }
        })
    },
    [TinyActionIconType.Delete]: {
        component: TrashSVG,
        testId: "delete"
    },
    [TinyActionIconType.ViewDetails]: {
        component: ViewDetailsSVG,
        testId: "view-details"
    },
    [TinyActionIconType.StartPicking]: {
        component: StartPickingSVG,
        testId: "start-picking"
    },
    [TinyActionIconType.PDF]: {
        component: PDFIcon,
        testId: "pdf"
    },
    [TinyActionIconType.PurchaseOrder]: {
        component: PurchsaeOrderSVG,
        testId: "purchase-order"
    },
    [TinyActionIconType.SalesOrder]: {
        component: SalesOrderSVG,
        testId: "sales-order",
        getProps: (variant) => ({
            ...getDefaultIconProps(variant),
            style: { fill: "#3C3769" }
        })
    },
    [TinyActionIconType.Email]: {
        component: EmailSVG,
        testId: "email"
    },
    [TinyActionIconType.StartReceiving]: {
        component: StartReceivingSVG,
        testId: "start-receiving"
    },
    [TinyActionIconType.Produce]: {
        component: ProduceSVG,
        testId: "produce"
    },
    [TinyActionIconType.Product]: {
        component: AllProductsIcon,
        testId: "product"
    },
    [TinyActionIconType.Duplicate]: {
        component: DuplicateSVG,
        testId: "duplicate"
    },
    [TinyActionIconType.Barcode]: {
        component: Barcode,
        testId: "barcode"
    },
    [TinyActionIconType.Copy]: {
        component: CopySVG,
        testId: "copy"
    },
    [TinyActionIconType.Cancel]: {
        component: Ban,
        testId: "cancel",
        getProps: (variant) => ({
            size: 20,
            className: variant === "gradient" ? "" : "text-accent"
        })
    }
};

export default function BaseTinyActionIcon({
                                               type,
                                               onClick,
                                               variant = "bordered",
                                               className,
                                               disabled,
                                               tooltip
                                           }: BaseTinyActionIconProps) {
    const iconConfig = iconConfigMap[type];
    if (!iconConfig) {
        return null;
    }

    const IconComponent = iconConfig.component;
    const iconProps = iconConfig.getProps
        ? iconConfig.getProps(variant)
        : getDefaultIconProps(variant);

    const iconElement = <IconComponent {...iconProps} />;

    // Style for the icon container
    const iconContainerStyle = variant === "gradient" ?
        {} : // Empty object for gradient style (no additional styling needed)
        {
            border: "1px solid transparent",
            background: "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, rgba(43, 42, 53, 1), rgba(105, 89, 252, 1)) border-box",
            borderRadius: "6px",
            height: "32px",
            width: "32px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            opacity: disabled ? 0.5 : 1
        };

    const el = <div 
        style={iconContainerStyle} 
        onClick={disabled ? undefined : onClick} 
        className={className}
        data-testid={`tiny-action-icon-${iconConfig.testId}`}
    >
        {iconElement}
    </div>;

    if (tooltip === undefined) return el;

    return (
        <BaseTooltip
            title={tooltip}
            placement={"bottom"}
            active={!disabled}
        >
            {el}
        </BaseTooltip>
    );
}

export { BaseTinyActionIcon, TinyActionIconType };
